'use client';

import React from 'react';

interface HeroSectionProps {
  onNavigate: (page: string) => void;
}

const HeroSection: React.FC<HeroSectionProps> = ({ onNavigate }) => {
  return (
    <div
      className="hero-section"
      style={{
        minHeight: 'calc(100vh - 64px)',
        display: 'flex',
        alignItems: 'center',
        padding: '3rem 1.5rem 3rem',
        position: 'relative',
      }}
    >
      {/* Background star decoration */}
      <div
        style={{
          position: 'absolute',
          right: '5%',
          top: '50%',
          transform: 'translateY(-50%)',
          fontSize: 'clamp(8rem, 22vw, 22rem)',
          color: 'rgba(244,196,48,0.04)',
          pointerEvents: 'none',
          fontFamily: 'serif',
          lineHeight: 1,
          userSelect: 'none',
        }}
      >
        ★
      </div>

      <div style={{ maxWidth: 680, position: 'relative', zIndex: 1, width: '100%' }}>
        {/* Badge */}
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: 'rgba(244,196,48,0.12)',
            border: '1px solid rgba(244,196,48,0.3)',
            color: '#f4c430',
            fontSize: '0.82rem',
            fontWeight: 600,
            padding: '0.4rem 1rem',
            borderRadius: 100,
            marginBottom: '1.5rem',
            letterSpacing: '0.05em',
          }}
        >
          ⭐ Trusted by 100+ Students
        </div>

        {/* Heading */}
        <h1
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 'clamp(2.2rem, 7vw, 4.5rem)',
            fontWeight: 900,
            lineHeight: 1.1,
            marginBottom: '1.25rem',
            color: '#ffffff',
          }}
        >
          Bring Out Your{' '}
          <em style={{ fontStyle: 'normal', color: '#f4c430' }}>Academic</em>{' '}
          Confidence
        </h1>

        {/* Quote */}
        <blockquote
          style={{
            margin: '1rem 0 1.25rem',
            borderLeft: '3px solid #f4c430',
            paddingLeft: '1.2rem',
          }}
        >
          <p
            style={{
              fontStyle: 'italic',
              color: 'rgba(255,255,255,0.75)',
              lineHeight: 1.7,
              marginBottom: '0.4rem',
              fontSize: '1rem',
            }}
          >
            &ldquo;Shoot for the moon. Even if you miss, you&rsquo;ll land among the stars.&rdquo;
          </p>
          <cite
            style={{
              fontSize: '0.82rem',
              color: '#f4c430',
              fontStyle: 'normal',
              fontWeight: 600,
            }}
          >
            — Norman Vincent Peale
          </cite>
        </blockquote>

        {/* Buttons */}
        <div className="hero-buttons" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <button
            className="btn-primary"
            onClick={() => onNavigate('signup')}
          >
            Get Started Today →
          </button>
          <button
            className="btn-outline"
            onClick={() => onNavigate('tutors')}
          >
            Meet Our Tutors
          </button>
        </div>

        {/* Stats */}
        <div
          className="hero-stats"
          style={{
            display: 'flex',
            gap: '2rem',
            marginTop: '3rem',
            paddingTop: '2rem',
            borderTop: '1px solid rgba(255,255,255,0.08)',
            flexWrap: 'wrap',
          }}
        >
          {[
            { num: '100+', label: 'Students Helped' },
            { num: '5.0★', label: 'Average Rating' },
            { num: '3+', label: 'Expert Tutors' },
            { num: '30+', label: 'Subjects Covered' },
          ].map((stat) => (
            <div key={stat.label} style={{ minWidth: 70 }}>
              <div
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: 'clamp(1.6rem, 4vw, 2.2rem)',
                  fontWeight: 700,
                  color: '#f4c430',
                }}
              >
                {stat.num}
              </div>
              <div
                style={{
                  fontSize: '0.82rem',
                  color: 'rgba(255,255,255,0.5)',
                  marginTop: '0.2rem',
                }}
              >
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Mission Statement */}
      <div
        className="mission-statement"
        style={{
          maxWidth: '860px',
          margin: '3.5rem auto 0',
          padding: '2.5rem 2rem',
          background: 'rgba(255,255,255,0.04)',
          borderRadius: '16px',
          border: '1px solid rgba(255,255,255,0.08)',
          textAlign: 'center',
        }}
      >
        <p
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 'clamp(1rem, 2.5vw, 1.2rem)',
            color: 'rgba(255,255,255,0.9)',
            lineHeight: 1.8,
            marginBottom: '1.25rem',
          }}
        >
          Stellar Scholar was founded in Edmonton, Alberta in 2021. It began as a single-person business and expanded through the years into a trusted community of experienced tutors.
        </p>
        <p
          style={{
            fontSize: 'clamp(0.9rem, 2vw, 1.05rem)',
            color: 'rgba(255,255,255,0.7)',
            lineHeight: 1.8,
            marginBottom: '1.25rem',
          }}
        >
          At Stellar Scholar, we believe every student learns differently and has the potential to excel. We provide individualized learning support to all ages and abilities, including students with learning differences such as ADHD and autism. We offer both in-person and online lessons across Edmonton and the surrounding area, and we offer online lessons across Canada.
        </p>
        <p
          style={{
            fontSize: 'clamp(0.9rem, 2vw, 1.05rem)',
            color: '#f4c430',
            fontWeight: 600,
            lineHeight: 1.8,
          }}
        >
          Take the first step toward your goals and register today.
        </p>
      </div>

      <style>{`
        @media (max-width: 640px) {
          .hero-section {
            flex-direction: column;
            align-items: flex-start;
          }
          .mission-statement {
            margin-top: 2rem !important;
            width: 100%;
          }
          .hero-buttons {
            flex-direction: column;
          }
          .hero-buttons .btn-primary,
          .hero-buttons .btn-outline {
            width: 100%;
            justify-content: center;
            text-align: center;
          }
          .hero-stats {
            gap: 1.25rem !important;
          }
        }
      `}</style>
    </div>
  );
};

export default HeroSection;
